import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  alpha,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  CollectionsBookmark as CollectionIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Collections as CollectionsIcon,
  AutoAwesome as PackIcon,
  Storefront as StoreIcon,
  CompareArrows as CompareArrowsIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Event as EventIcon,
  EmojiEvents as TrophyIcon,
  SettingsSystemDaydream as SetsIcon,
  Dashboard as DashboardIcon,
  Sell as SellIcon,
  FactCheck as GradingIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthDialog from '../../components/AuthDialog';
import LazyImage from '../../components/LazyImage';
import { useThemeContext } from '../../contexts/ThemeContext';
import ImmersiveBackground from '../../components/ImmersiveBackground';
import { typeColors } from '../../theme/typeColors';
import MarketHighlights from '../../components/LandingPage/MarketHighlights';
import SEO from '../../components/SEO';

// Mock data for "Chase Cards" showcase
const chaseCards = [
  {
    id: 'base1-4',
    name: 'Charizard',
    number: '4/102',
    image: 'https://images.pokemontcg.io/base1/4_hires.png',
    rarity: 'Rare Holo',
  },
  {
    id: 'op05-060',
    name: 'Monkey D. Luffy (Gear 5)',
    number: 'OP05-060',
    image: '/images/onepiece/OP-13/thumbnail/OP13-118_p3.webp',
    rarity: 'SEC',
  },
  {
    id: 'sv3pt5-151',
    name: 'Mew ex',
    number: '151/165',
    image: 'https://images.pokemontcg.io/sv3pt5/151_hires.png',
    rarity: 'Double Rare',
  },
];


const TiltCard = ({ image, name }: { image: string; name: string }) => {
  return (
    <Box
      sx={{
        perspective: '1000px',
        '&:hover .inner': {
          transform: 'rotateY(10deg) rotateX(5deg) scale(1.05)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        },
        cursor: 'pointer',
      }}
    >
      <Box
        className="inner"
        sx={{
          transition: 'all 0.4s ease',
          transformStyle: 'preserve-3d',
          borderRadius: '14px',
          position: 'relative',
          boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
        }}
      >
        <Box
          component="img"
          src={image}
          alt={name}
          sx={{
            width: '100%',
            maxWidth: 240,
            borderRadius: '14px',
            display: 'block',
          }}
        />
        {/* Holographic overlay effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '14px',
            background:
              'linear-gradient(125deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.1) 60%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none',
            mixBlendMode: 'overlay',
          }}
        />
      </Box>
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user, isAuthModalOpen, setAuthModalOpen, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { mode, toggleTheme } = useThemeContext();
  
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    "Collection": false,
    "Events & Tourneys": false,
  });

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  // Animation state
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/sets');
    } else {
      setAuthModalOpen(true);
    }
  };

  const features = [
    {
      icon: <CollectionIcon sx={{ fontSize: 40, color: typeColors.grass }} />,
      title: 'Collection Mastery',
      description:
        'Track every card with precision. Organize by set, rarity, and condition.',
      color: typeColors.grass,
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: typeColors.fire }} />,
      title: 'Market Intelligence',
      description:
        'Real-time TCGPlayer price data to monitor your portfolio value instantly.',
      color: typeColors.fire,
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: typeColors.water }} />,
      title: 'Deep Analytics',
      description:
        'Visualize your collection growth, distribution, and completion progress.',
      color: typeColors.water,
    },
    {
      icon: <SearchIcon sx={{ fontSize: 40, color: typeColors.lightning }} />,
      title: 'Lightning Search',
      description:
        'Find any card instantly across all sets with our optimized search.',
      color: typeColors.lightning,
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: typeColors.metal }} />,
      title: 'Secure Vault',
      description:
        'Your collection data is encrypted and backed up daily for peace of mind.',
      color: typeColors.metal,
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: typeColors.psychic }} />,
      title: 'Instant Sync',
      description:
        'Synchronize your tracking across all devices with real-time cloud updates.',
      color: typeColors.psychic,
    },
  ];

  return (
    <React.Fragment>
      <SEO 
        title="Home" 
        description="The ultimate hub for TCG professionals. Track your Pokémon and One Piece card collection, monitor market prices, and never miss a restock drop."
        keywords="TCG collection tracker, Pokemon card value, One Piece TCG tracking, restock alerts, chasing tcg"
      />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ImmersiveBackground />

      {/* Header / AppBar */}
      <AppBar 
        position="fixed" 
        color="transparent" 
        elevation={0} 
        sx={{ 
          transition: 'all 0.3s ease',
          backdropFilter: isScrolled ? 'blur(20px)' : 'none',
          background: isScrolled 
            ? (theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.7) : alpha(theme.palette.background.default, 0.8)) 
            : 'transparent',
          borderBottom: isScrolled ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : '1px solid transparent',
          py: isScrolled ? 0.5 : 1.5,
        }}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => setDrawerOpen(true)} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, cursor: 'pointer', fontWeight: 'bold' }} onClick={() => navigate('/')}>
            Chasing TCG
          </Typography>
          <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 2 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          {isAuthenticated ? (
            <>
              <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem disabled>
                  <Typography variant="body2">{user?.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard'); }}>
                  <ListItemIcon><TrendingUpIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Dashboard</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>
                  <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { signOut(); setAnchorEl(null); }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Sign Out</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="inherit" variant="outlined" onClick={() => setAuthModalOpen(true)}>
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Temporary Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Toolbar>
            <Typography variant="h6" fontWeight="bold" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
              Chasing TCG
            </Typography>
          </Toolbar>
          <List sx={{ pt: 0 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/search'); }}>
                <ListItemIcon><SearchIcon /></ListItemIcon>
                <ListItemText primary="Search" />
              </ListItemButton>
            </ListItem>
            
            <Divider sx={{ mx: 2, my: 1, opacity: 0.5 }} />

            {/* Collector's Hub */}
            <Typography
              variant="caption"
              sx={{ px: 3, display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}
            >
              Collector's Hub
            </Typography>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleGroup("Collection")}>
                <ListItemIcon><CollectionIcon /></ListItemIcon>
                <ListItemText primary="Collection" />
                {openGroups["Collection"] ? <ExpandLess sx={{ color: 'text.secondary', fontSize: 18 }} /> : <ExpandMore sx={{ color: 'text.secondary', fontSize: 18 }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openGroups["Collection"]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/collection'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><CollectionIcon /></ListItemIcon>
                    <ListItemText primary="My Collection" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/trades/history'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><CompareArrowsIcon /></ListItemIcon>
                    <ListItemText primary="Trades" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/buy-list'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><ShoppingCartIcon /></ListItemIcon>
                    <ListItemText primary="Buy List" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/grading'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><GradingIcon /></ListItemIcon>
                    <ListItemText primary="Grading" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/sold'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><SellIcon /></ListItemIcon>
                    <ListItemText primary="Sold Inventory" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/master-sets'); }}>
                <ListItemIcon><SetsIcon /></ListItemIcon>
                <ListItemText primary="Master Sets" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/onepiece/deck-builder'); }}>
                <ListItemIcon><DashboardIcon /></ListItemIcon>
                <ListItemText primary="Deck Builder" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/packs'); }}>
                <ListItemIcon><PackIcon /></ListItemIcon>
                <ListItemText primary="Pack Opening" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/drops'); }}>
                <ListItemIcon><InventoryIcon /></ListItemIcon>
                <ListItemText primary="TCG Drops" />
              </ListItemButton>
            </ListItem>
            
            <Divider sx={{ mx: 2, my: 1, opacity: 0.5 }} />

            {/* Shop & Play */}
            <Typography
              variant="caption"
              sx={{ px: 3, display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}
            >
              Shop & Play
            </Typography>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/store'); }}>
                <ListItemIcon><StoreIcon /></ListItemIcon>
                <ListItemText primary="Public Store" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleGroup("Events & Tourneys")}>
                <ListItemIcon><EventIcon /></ListItemIcon>
                <ListItemText primary="Events & Tourneys" />
                {openGroups["Events & Tourneys"] ? <ExpandLess sx={{ color: 'text.secondary', fontSize: 18 }} /> : <ExpandMore sx={{ color: 'text.secondary', fontSize: 18 }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openGroups["Events & Tourneys"]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/events'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><EventIcon /></ListItemIcon>
                    <ListItemText primary="Events" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/tournaments'); }} sx={{ pl: 4 }}>
                    <ListItemIcon><TrophyIcon /></ListItemIcon>
                    <ListItemText primary="Tournaments" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 20 },
          pb: { xs: 8, md: 16 },
          position: 'relative',
        }}
      >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 6 } }}>
            <Box sx={{ textAlign: 'center', maxWidth: 850, mx: 'auto' }}>
              
              {/* Animated Chip */}
              <Box
                sx={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    px: 2,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <PackIcon fontSize="small" /> The Ultimate Collector's Hub
                </Box>
              </Box>

              {/* Main Headline with Gradient */}
              <Typography
                variant="h1"
                sx={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
                  fontWeight: 900,
                  mb: 3,
                  fontSize: { xs: '3rem', sm: '4rem', md: '5.5rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  background: theme.palette.mode === 'dark' 
                    ? `linear-gradient(to right, #fff, ${alpha('#fff', 0.7)})` 
                    : `linear-gradient(to right, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Master Your <br />
                TCG Collection
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h4"
                sx={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  color: 'text.secondary',
                  mb: 6,
                  fontWeight: 400,
                  maxWidth: 650,
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Professional-grade tools to track value, complete sets, and dominate the market. Experience the most advanced TCG platform ever built.
              </Typography>

              {/* Glassmorphic Search Bar */}
              <Box 
                component="form" 
                onSubmit={handleSearch} 
                sx={{ 
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.3s',
                  mb: 5, 
                  width: '100%', 
                  maxWidth: 700, 
                  mx: 'auto' 
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search for any card, set, or character..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', ml: 1, fontSize: 28 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          onClick={handleSearch}
                          disableElevation
                          sx={{
                            minWidth: 120,
                            py: 1.5,
                            borderRadius: '12px',
                            fontWeight: 800,
                            mr: -0.5,
                            textTransform: 'none',
                            fontSize: '1rem',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }
                          }}
                        >
                          Search
                        </Button>
                      </InputAdornment>
                    ),
                    sx: {
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.background.paper, 0.9),
                      backdropFilter: 'blur(20px)',
                      borderRadius: '16px',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      color: 'text.primary',
                      fontSize: '1.1rem',
                      py: 0.5,
                      pl: 1,
                      boxShadow: theme.palette.mode === 'light' ? '0 12px 40px rgba(0,0,0,0.08)' : `0 12px 40px ${alpha('#000', 0.5)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'light' ? '0 16px 50px rgba(0,0,0,0.12)' : `0 16px 50px ${alpha('#000', 0.7)}`,
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    },
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s',
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  disableElevation
                  sx={{
                    fontSize: '1.05rem',
                    px: 5,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 800,
                    width: { xs: '100%', sm: 'auto' },
                    textTransform: 'none',
                    bgcolor: 'text.primary',
                    color: 'background.paper',
                    '&:hover': {
                      bgcolor: 'text.secondary',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  Start Your Journey
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/sets')}
                  sx={{
                    fontSize: '1.05rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    borderWidth: 2,
                    borderColor: alpha(theme.palette.divider, 0.8),
                    color: 'text.primary',
                    fontWeight: 700,
                    width: { xs: '100%', sm: 'auto' },
                    textTransform: 'none',
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Explore Sets
                </Button>
              </Stack>
            </Box>
          </Container>
      </Box>



        {/* Market Highlights Section */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, mt: -8, mb: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <MarketHighlights game="pokemon" />
            </Box>
        </Container>

        {/* Features Section */}
        <Box sx={{ py: 12, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                mb: 8, 
                color: 'text.primary', 
                fontWeight: 800,
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.5s',
              }}
            >
              Why Collectors Choose Us
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      opacity: mounted ? 1 : 0,
                      transform: mounted ? 'translateY(0)' : 'translateY(40px)',
                      transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.6 + index * 0.1}s`,
                      background: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.4)
                        : alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      boxShadow: theme.palette.mode === 'light' ? '0 12px 30px rgba(0,0,0,0.03)' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${feature.color}, ${alpha(feature.color, 0.5)})`,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        borderColor: alpha(feature.color, 0.5),
                        boxShadow: `0 20px 40px ${alpha(feature.color, 0.15)}`,
                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : theme.palette.background.paper,
                        '&::before': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{
                        mb: 3,
                        display: 'inline-flex',
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: alpha(feature.color, 0.1),
                        color: feature.color,
                        boxShadow: `inset 0 0 0 1px ${alpha(feature.color, 0.2)}`,
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: 'text.primary' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

      </Box>
      <AuthDialog
        open={isAuthModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </React.Fragment>
  );
};

export default LandingPage;
